import { useState } from "react";
import { Download, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { formatDate, formatNumber } from "@/lib/format";
import { PasswordDialog } from "@/components/settings/backup/PasswordDialog";
import { RestoreDialog } from "@/components/settings/backup/RestoreDialog";
import { TYPE_LABEL, blobError, downloadBackup, kb } from "@/components/settings/backup/util";

const Tags = ({ tags }) => (
  <div className="flex flex-wrap gap-1">{tags.map((t) => <span key={t} className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{TYPE_LABEL[t] || t}</span>)}</div>
);

const Row = ({ b, isSuper, onAction, onKeep }) => (
  <tr className="border-b border-slate-100 last:border-0 text-[12px]" data-testid={`backup-row-${b.id}`}>
    <td className="py-2 pr-3 whitespace-nowrap">{formatDate(b.started_at)}</td>
    <td className="pr-3"><Tags tags={b.tags} /></td>
    <td className="pr-3 font-mono text-[11px] break-all">{b.name}</td>
    <td className="pr-3 whitespace-nowrap">{b.deleted ? <span className="text-slate-400">Silindi</span> : kb(b.size_bytes)}</td>
    <td className="pr-3">{formatNumber(b.documents)}</td>
    <td className="pr-3">{b.verified ? <span className="text-emerald-700">Doğrulandı</span> : "—"}</td>
    <td className="pr-3">{b.s3?.label || "—"}</td>
    <td className="pr-3">{b.by}</td>
    <td className="pr-3 whitespace-nowrap">
      {isSuper ? (
        <label className="inline-flex items-center gap-1 cursor-pointer" title={b.keep && !b.keep_flag ? "Kilometre taşı yedeği (pre_) — her zaman kalıcı" : "Otomatik saklamadan muaf tut"}>
          <input type="checkbox" checked={!!b.keep} disabled={b.keep && !b.keep_flag} onChange={(e) => onKeep(b, e.target.checked)} data-testid={`backup-keep-${b.id}`} />
          <span className={b.keep ? "text-emerald-700 font-semibold" : "text-slate-500"}>Kalıcı</span>
        </label>
      ) : b.keep ? <span className="text-emerald-700 font-semibold">Kalıcı</span> : "—"}
    </td>
    <td className="text-right whitespace-nowrap">
      {isSuper && !b.deleted && <>
        <Button size="sm" variant="ghost" onClick={() => onAction("download", b)} data-testid={`backup-download-${b.id}`}><Download className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="ghost" onClick={() => onAction("restore", b)} data-testid={`backup-restore-${b.id}`}><RotateCcw className="h-3.5 w-3.5" /></Button>
        <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => onAction("delete", b)} data-testid={`backup-delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
      </>}
    </td>
  </tr>
);

export function HistoryTable({ d, onDone }) {
  const [act, setAct] = useState(null);
  const keep = async (b, v) => {
    try { await api.post(`/backup/${b.id}/keep`, { keep: v }); toast.success(v ? `${b.name} kalıcı` : "Kalıcı işareti kaldırıldı"); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pwAction = async (pw) => {
    try {
      if (act.kind === "download") toast.success(`${await downloadBackup(act.b.id, pw)} indirildi (şifreli)`);
      else { await api.post(`/backup/${act.b.id}/delete`, { password: pw }); toast.success("Yedek silindi"); onDone(); }
      return true;
    } catch (e) { toast.error((await blobError(e)) || apiError(e)); return false; }
  };
  return (
    <div className="panel p-4" data-testid="backup-history">
      <h3 className="font-semibold mb-2">Yedek Geçmişi</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead><tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
            {["Tarih", "Tür", "Dosya", "Boyut", "Belge", "Bütünlük", "Harici kopya", "Alan", "Kalıcı", ""].map((h) => <th key={h} className="py-2 pr-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>{d.history.map((b) => <Row key={b.id} b={b} isSuper={d.is_super} onAction={(kind, x) => setAct({ kind, b: x })} onKeep={keep} />)}</tbody>
        </table>
        {!d.history.length && <div className="text-[13px] text-slate-500 py-4" data-testid="backup-history-empty">Henüz yedek yok</div>}
      </div>
      {d.failures.length > 0 && (
        <div className="mt-4" data-testid="backup-failures">
          <div className="font-semibold text-[13px] text-rose-700 mb-1">Başarısız yedekler <span className="font-normal text-slate-600" data-testid="backup-fail-stats">· {d.alert.stats?.label}</span></div>
          {d.failures.map((f) => <div key={f.id} className="text-[12px] text-slate-600">{formatDate(f.started_at)} · {TYPE_LABEL[f.type] || f.type} · {f.error}
            {f.simulated ? <span className="ml-1 text-[10px] px-1 rounded border border-amber-300 bg-amber-50 text-amber-800" data-testid={`backup-failure-sim-${f.id}`}>Simülasyon</span>
              : <span className="ml-1 text-[10px] px-1 rounded border border-rose-300 bg-rose-50 text-rose-700">Gerçek</span>}</div>)}
        </div>
      )}
      <PasswordDialog open={act?.kind === "download" || act?.kind === "delete"} onOpenChange={(v) => !v && setAct(null)} danger={act?.kind === "delete"}
        title={act?.kind === "delete" ? "Yedeği Sil" : "Şifreli Yedeği İndir"} confirmLabel={act?.kind === "delete" ? "Sil" : "İndir"}
        description={act?.kind === "delete" ? `${act?.b.name} yerel diskten kalıcı olarak silinecek.` : "Dosya AES-256-GCM ile şifrelidir; açmak için şifreleme anahtarı gerekir. İşlem denetim kaydına yazılır."}
        onConfirm={pwAction} />
      <RestoreDialog backup={act?.kind === "restore" ? act.b : null} onClose={() => setAct(null)} onDone={onDone} />
    </div>
  );
}
